import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  TextField,
  Autocomplete,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { TrendingUpOutlined, SearchOutlined } from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { api } from '../../services/api';
import { Category, Partner, Item } from '../../types/api';
import { INDIA_STATES } from '../../constants/indiaLocations';
import { reportService, SalesGraphSeries } from '../../services/reportService';

// Fixed-order categorical palette (see dataviz skill references/palette.md) — never cycled or reassigned.
// Beyond slot 8, distinct hues run out and later slots fall back to lighter tints of the earlier
// hues — an accepted tradeoff to fit 20 concurrent series rather than folding into "Other" sooner.
const CATEGORICAL_PALETTE = [
  '#2a78d6', // blue
  '#1baf7a', // aqua
  '#eda100', // yellow
  '#008300', // green
  '#4a3aa7', // violet
  '#e34948', // red
  '#e87ba4', // magenta
  '#eb6834', // orange
  '#17becf', // cyan
  '#8c564b', // brown
  '#7f7f7f', // gray
  '#bcbd22', // olive
  '#9467bd', // purple
  '#aec7e8', // light blue
  '#98df8a', // light green
  '#ffbb78', // light orange
  '#c5b0d5', // light violet
  '#ff9896', // light red
  '#c49c94', // light brown
  '#9edae5', // light cyan
];

const OTHER_SERIES_KEY = 'Other';

const formatDateInput = (date: Date): string =>
  date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');

const defaultStartDate = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return formatDateInput(d);
};

const defaultEndDate = (): string => formatDateInput(new Date());

// The API only emits a bucket when at least one series had activity that day/month — a bucket
// absent from the response means zero, not "no data", so the full range must be generated here
// rather than derived from whatever dates happen to appear in the response.
const generateBucketSequence = (startDate: string, endDate: string, duration: 'day' | 'month'): string[] => {
  const buckets: string[] = [];

  if (duration === 'month') {
    const [startYear, startMonth] = startDate.split('-').map(Number);
    const [endYear, endMonth] = endDate.split('-').map(Number);
    let year = startYear;
    let month = startMonth;
    while (year < endYear || (year === endYear && month <= endMonth)) {
      buckets.push(`${year}-${String(month).padStart(2, '0')}`);
      month += 1;
      if (month > 12) {
        month = 1;
        year += 1;
      }
    }
  } else {
    const cursor = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');
    while (cursor <= end) {
      buckets.push(formatDateInput(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
  }

  return buckets;
};

// Folds low-volume series beyond the palette limit into a single "Other" series (never a generated hue),
// then pivots the API's per-series point lists into one flat, bucket-keyed row per chart X position —
// zero-filling any bucket a series has no point for, so gaps read as zero sales rather than being
// bridged by a connecting line.
const buildChartData = (
  series: SalesGraphSeries[],
  startDate: string,
  endDate: string,
  duration: 'day' | 'month'
): { rows: Record<string, string | number>[]; seriesKeys: string[] } => {
  let working = series;

  if (series.length > CATEGORICAL_PALETTE.length) {
    const withTotals = series
      .map((s) => ({ s, total: s.points.reduce((sum, p) => sum + p.quantity, 0) }))
      .sort((a, b) => b.total - a.total);

    const top = withTotals.slice(0, CATEGORICAL_PALETTE.length - 1).map((w) => w.s);
    const rest = withTotals.slice(CATEGORICAL_PALETTE.length - 1).map((w) => w.s);

    const otherByDate = new Map<string, number>();
    rest.forEach((s) =>
      s.points.forEach((p) => {
        otherByDate.set(p.date, (otherByDate.get(p.date) || 0) + p.quantity);
      })
    );

    working = [
      ...top,
      {
        key: OTHER_SERIES_KEY,
        points: Array.from(otherByDate.entries()).map(([date, quantity]) => ({ date, quantity })),
      },
    ];
  }

  const seriesKeys = working.map((s) => s.key);
  const valuesByKeyAndDate = new Map<string, Map<string, number>>();

  working.forEach((s) => {
    const byDate = new Map<string, number>();
    s.points.forEach((p) => {
      byDate.set(p.date, p.quantity);
    });
    valuesByKeyAndDate.set(s.key, byDate);
  });

  const buckets = generateBucketSequence(startDate, endDate, duration);
  const rows = buckets.map((date) => {
    const row: Record<string, string | number> = { date };
    seriesKeys.forEach((key) => {
      row[key] = valuesByKeyAndDate.get(key)?.get(date) ?? 0;
    });
    return row;
  });

  return { rows, seriesKeys };
};

const SalesGraphReport: React.FC = () => {
  // Filters
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>(defaultStartDate());
  const [endDate, setEndDate] = useState<string>(defaultEndDate());
  const [duration, setDuration] = useState<'day' | 'month'>('day');
  const [groupBy, setGroupBy] = useState<'none' | 'category' | 'product'>('none');

  // Dropdown options
  const [categories, setCategories] = useState<Category[]>([]);
  const [allItems, setAllItems] = useState<Item[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingPartners, setLoadingPartners] = useState(true);

  // Result state
  const [series, setSeries] = useState<SalesGraphSeries[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The exact range/duration the current `series` was fetched with — used to zero-fill the chart's
  // bucket sequence so it can't drift if startDate/endDate/duration change after searching but before
  // the next Search click.
  const [queriedRange, setQueriedRange] = useState<{ startDate: string; endDate: string; duration: 'day' | 'month' } | null>(
    null
  );
  // Series keys toggled off via a legend click — Recharts doesn't wire this up itself.
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get<{ categories: Category[] }>('/api/categories');
        setCategories(response.data.categories || []);
      } catch (err) {
        console.error('Error fetching categories:', err);
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await api.get<{ partners: Partner[] }>('/api/partners', { params: { type: 'CUSTOMER' } });
        setPartners(response.data.partners || []);
      } catch (err) {
        console.error('Error fetching partners:', err);
      } finally {
        setLoadingPartners(false);
      }
    };
    fetchPartners();
  }, []);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const response = await api.get<{ items: Item[] }>('/api/items');
        setAllItems(response.data.items || []);
      } catch (err) {
        console.error('Error fetching items:', err);
      }
    };
    fetchItems();
  }, []);

  // Reset the selected item whenever the category changes so a stale item from a different category can't linger
  useEffect(() => {
    setSelectedItem(null);
  }, [selectedCategory]);

  const filteredItems = selectedCategory ? allItems.filter((item) => item.category_id === selectedCategory.id) : allItems;

  const handleSearch = async () => {
    setLoading(true);
    setHasSearched(true);
    setError(null);
    setHiddenKeys(new Set());

    try {
      const response = await reportService.getSalesGraph({
        start_date: startDate,
        end_date: endDate,
        category_id: selectedCategory?.id,
        item_id: selectedItem?.id,
        partner_id: selectedPartner?.id,
        state: selectedState || undefined,
        duration,
        group_by: groupBy,
      });
      setSeries(response.series || []);
      setQueriedRange({ startDate, endDate, duration });
    } catch (err) {
      console.error('Error fetching sales graph:', err);
      setError('Failed to load sales graph. Please try again.');
      setSeries([]);
      setQueriedRange(null);
    } finally {
      setLoading(false);
    }
  };

  const { rows, seriesKeys } = useMemo(
    () =>
      queriedRange
        ? buildChartData(series, queriedRange.startDate, queriedRange.endDate, queriedRange.duration)
        : { rows: [], seriesKeys: [] },
    [series, queriedRange]
  );
  const canSearch = Boolean(startDate && endDate);

  return (
    <Paper sx={{ p: 2, m: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <TrendingUpOutlined sx={{ fontSize: 28, mr: 1.5, color: 'primary.main' }} />
        <Typography variant="h6" fontWeight="bold">
          Sales Graph
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Visualize completed sales quantity over time, optionally filtered by category, customer or state.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Autocomplete
            options={categories}
            getOptionLabel={(option) => option.name}
            value={selectedCategory}
            onChange={(_event, newValue) => setSelectedCategory(newValue)}
            loading={loadingCategories}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Category"
                placeholder="All categories"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCategories ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Autocomplete
            options={filteredItems}
            getOptionLabel={(option) => option.name}
            value={selectedItem}
            onChange={(_event, newValue) => setSelectedItem(newValue)}
            renderInput={(params) => <TextField {...params} size="small" label="Product" placeholder="All products" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Autocomplete
            options={partners}
            getOptionLabel={(option) => `${option.name} (${option.type})`}
            value={selectedPartner}
            onChange={(_event, newValue) => setSelectedPartner(newValue)}
            loading={loadingPartners}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Customer"
                placeholder="All customers"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingPartners ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Autocomplete
            options={INDIA_STATES}
            value={selectedState}
            onChange={(_event, newValue) => setSelectedState(newValue)}
            renderInput={(params) => <TextField {...params} size="small" label="State" placeholder="All states" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="sales-graph-duration-label">Duration</InputLabel>
            <Select
              labelId="sales-graph-duration-label"
              label="Duration"
              value={duration}
              onChange={(e: SelectChangeEvent) => setDuration(e.target.value as 'day' | 'month')}
            >
              <MenuItem value="day">Daily</MenuItem>
              <MenuItem value="month">Monthly</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="sales-graph-groupby-label">Group By</InputLabel>
            <Select
              labelId="sales-graph-groupby-label"
              label="Group By"
              value={groupBy}
              onChange={(e: SelectChangeEvent) => setGroupBy(e.target.value as 'none' | 'category' | 'product')}
            >
              <MenuItem value="none">None (Total)</MenuItem>
              <MenuItem value="category">Category</MenuItem>
              <MenuItem value="product">Product</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <TextField
            size="small"
            fullWidth
            label="Start Date *"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <TextField
            size="small"
            fullWidth
            label="End Date *"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSearch}
            disabled={!canSearch || loading}
            startIcon={<SearchOutlined />}
            sx={{ height: '40px' }}
          >
            Search
          </Button>
        </Grid>
      </Grid>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Box>
      ) : !hasSearched ? (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Select a date range, then click Search to view the sales graph
          </Typography>
        </Box>
      ) : series.length === 0 ? (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No completed sales found for the selected criteria
          </Typography>
        </Box>
      ) : (
        <Box sx={{ height: 420 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
              <CartesianGrid stroke="#e1e0d9" strokeDasharray="0" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fill: '#898781', fontSize: 12 }}
                axisLine={{ stroke: '#c3c2b7' }}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fill: '#898781', fontSize: 12 }}
                axisLine={{ stroke: '#c3c2b7' }}
                tickLine={false}
              />
              <Tooltip />
              {seriesKeys.length > 1 && (
                <Legend
                  onClick={(entry) => {
                    const key = String(entry.dataKey);
                    setHiddenKeys((prev) => {
                      const next = new Set(prev);
                      if (next.has(key)) {
                        next.delete(key);
                      } else {
                        next.add(key);
                      }
                      return next;
                    });
                  }}
                  formatter={(value) => (
                    <span
                      style={{
                        cursor: 'pointer',
                        textDecoration: hiddenKeys.has(value) ? 'line-through' : 'none',
                        color: hiddenKeys.has(value) ? '#898781' : undefined,
                      }}
                    >
                      {value}
                    </span>
                  )}
                />
              )}
              {seriesKeys.map((key, index) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  name={key}
                  stroke={CATEGORICAL_PALETTE[index % CATEGORICAL_PALETTE.length]}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  hide={hiddenKeys.has(key)}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
};

export default SalesGraphReport;
